import * as React from 'react';

import { Form, Input, notification, Spin } from 'antd';
import {
  Checkbox,
  InfoCircleOutlined,
  Input as CInput,
  Row,
  SelectOption,
  Space,
} from 'farmhub-ui-core';

import { useAppSelector } from 'app';
import {
  CSelect,
  dataEmpty,
  Empty,
  InfoCard,
  NameText,
  PhoneNumberText,
} from 'components';
import { selectAppDevice } from 'slice';
import { theme } from 'styled';
import {
  CHAIN_ENTITY_ROLE,
  DEVICES,
  ENTITY_TYPE,
  GROUP_TYPE,
  SIZE,
} from 'utils';
import {
  checkFirstCharIsNotSpace,
  checkOnlySpaces,
  genPartnerCodeFromEntityCode,
} from 'utils/function';

const groupTypePartner = {
  // based on type of current user
  SUPPLIER: GROUP_TYPE.CUSTOMER,
  CUSTOMER: GROUP_TYPE.SUPPLIER,
};

const entityType = ENTITY_TYPE.COLLABORATOR;
// 0: 'user', 1: 'farm', 2: 'store', 3: 'collaborator', 4: 'warehouse', 5: 'transport', 6: 'volunteer'

const PartnerInfoStep = ({
  form,
  partnerList,
  setPartnerList,
  currentPartnerGroupType,
  setCurrentPartnerGroupType,
  currentPartnerID,
  setCurrentPartnerID,
  selectedPartner,
  setSelectedPartner,
  setKeySearchPartner,
  isLoadingSearchPartner,
  setSelectedProducts,
}: PartnerInfoStepProps): JSX.Element => {
  const appDevice = useAppSelector(selectAppDevice);

  const [shouldCreateDigitalMenu, setShouldCreateDigitalMenu] =
    React.useState(false);

  const genTypeList = (
    type,
    partnerType,
    validRole?: CHAIN_ENTITY_ROLE
  ): { key: string; value: string; name: string }[] => {
    const getRoles = () => {
      switch (partnerType) {
        case ENTITY_TYPE.FARM:
          if (type === ENTITY_TYPE.FARM) {
            return [
              {
                key: 'SUPPLIER',
                value: 'SUPPLIER',
                name: 'Nhà cung cấp',
              },
              {
                key: 'CUSTOMER',
                value: 'CUSTOMER',
                name: 'Khách hàng',
              },
            ];
          }
          return [
            {
              key: 'CUSTOMER',
              value: 'CUSTOMER',
              name: 'Khách hàng',
            },
          ];
        case ENTITY_TYPE.STORE:
          switch (type) {
            case ENTITY_TYPE.FARM:
              return [
                {
                  key: 'SUPPLIER',
                  value: 'SUPPLIER',
                  name: 'Nhà cung cấp',
                },
              ];
            case ENTITY_TYPE.STORE:
              return [
                {
                  key: 'SUPPLIER',
                  value: 'SUPPLIER',
                  name: 'Nhà cung cấp',
                },
                {
                  key: 'CUSTOMER',
                  value: 'CUSTOMER',
                  name: 'Khách hàng',
                },
              ];
            case ENTITY_TYPE.COLLABORATOR:
              return [
                {
                  key: 'CUSTOMER',
                  value: 'CUSTOMER',
                  name: 'Khách hàng',
                },
              ];
            default:
              return [];
          }
        case ENTITY_TYPE.COLLABORATOR:
          switch (type) {
            case ENTITY_TYPE.COLLABORATOR:
              return [
                {
                  key: 'SUPPLIER',
                  value: 'SUPPLIER',
                  name: 'Nhà cung cấp',
                },
                {
                  key: 'CUSTOMER',
                  value: 'CUSTOMER',
                  name: 'Khách hàng',
                },
              ];
            default:
              return [
                {
                  key: 'SUPPLIER',
                  value: 'SUPPLIER',
                  name: 'Nhà cung cấp',
                },
              ];
          }
        default:
          return [
            {
              key: 'SUPPLIER',
              value: 'SUPPLIER',
              name: 'Nhà cung cấp',
            },
            {
              key: 'CUSTOMER',
              value: 'CUSTOMER',
              name: 'Khách hàng',
            },
          ];
      }
    };

    if (getRoles().length === 1) {
      return getRoles();
    }

    if (validRole !== CHAIN_ENTITY_ROLE.INVALID) {
      switch (validRole) {
        case CHAIN_ENTITY_ROLE.CUSTOMER: {
          return [
            {
              key: 'CUSTOMER',
              value: 'CUSTOMER',
              name: 'Khách hàng',
            },
          ];
        }
        case CHAIN_ENTITY_ROLE.SUPPLIER: {
          return [
            {
              key: 'SUPPLIER',
              value: 'SUPPLIER',
              name: 'Nhà cung cấp',
            },
          ];
        }
        default:
          return [
            {
              key: 'CUSTOMER',
              value: 'CUSTOMER',
              name: 'Khách hàng',
            },
            {
              key: 'SUPPLIER',
              value: 'SUPPLIER',
              name: 'Nhà cung cấp',
            },
          ];
      }
    }

    return getRoles();
  };

  const handleFormChange = (value: any): void => {
    if (Object.keys(value).includes('entityID')) {
      setCurrentPartnerID(value.entityID);
    }
    if (value.entityID) {
      setSelectedProducts([]);
      const index = partnerList.findIndex(item => item.id === value.entityID);
      if (index != -1) {
        setSelectedPartner(partnerList[index]);

        form.setFieldsValue({
          type: genTypeList(
            entityType,
            partnerList[index].type,
            partnerList[index].validRole
          )[0].value,
          partnerCode: genPartnerCodeFromEntityCode(partnerList[index].code),
        });
        setCurrentPartnerGroupType(
          groupTypePartner[
            genTypeList(
              entityType,
              partnerList[index].type,
              partnerList[index].validRole
            )[0].value
          ]
        );
      }
    }

    if (value.type) {
      setCurrentPartnerGroupType(value.type);
    }
  };

  const handleFocus = (type): void | null => {
    if (appDevice.device === DEVICES.DESKTOP) {
      switch (type) {
        case 'NAME':
          return notification.info({
            message: `Tìm đối tác`,
            description:
              'Tìm tất cả các đối tác trên hệ thống mà bạn có thể liên kết',
            placement: 'bottomRight',
            duration: 10,
            icon: <InfoCircleOutlined style={{ color: '#1890FF' }} />,
            style: {
              border: '1px solid #91d5ff',
              backgroundColor: '#e6f7ff',
            },
          });
        case 'SKU':
          return notification.info({
            message: `SKU Đối tác`,
            description: 'Thêm SKU đối tác',
            placement: 'bottomRight',
            duration: 10,
            icon: <InfoCircleOutlined style={{ color: '#1890FF' }} />,
            style: {
              border: '1px solid #91d5ff',
              backgroundColor: '#e6f7ff',
            },
          });
        case 'ROLL':
          return notification.info({
            message: `Vai trò liên kết`,
            description:
              'Bạn có thể trở thành nhà cung cấp hoặc khách hàng của đối tác',
            placement: 'bottomRight',
            duration: 10,
            icon: <InfoCircleOutlined style={{ color: '#1890FF' }} />,
            style: {
              border: '1px solid #91d5ff',
              backgroundColor: '#e6f7ff',
            },
          });
        case 'GROUP':
          return notification.info({
            message: `Thêm đối tác vào nhóm`,
            description:
              'Sau khi liên kết, mỗi đối tác phải được thêm vào một nhóm tương ứng',
            placement: 'bottomRight',
            duration: 10,
            icon: <InfoCircleOutlined style={{ color: '#1890FF' }} />,
            style: {
              border: '1px solid #91d5ff',
              backgroundColor: '#e6f7ff',
            },
          });
        case 'INTRODUCE':
          return notification.info({
            message: `Lời giới thiệu`,
            description: 'Hãy viết vài dòng giới thiệu về bản thân',
            placement: 'bottomRight',
            duration: 10,
            icon: <InfoCircleOutlined style={{ color: '#1890FF' }} />,
            style: {
              border: '1px solid #91d5ff',
              backgroundColor: '#e6f7ff',
            },
          });
        case 'PRICE_POLICY':
          return notification.info({
            message: `Chính sách giá bán`,
            description:
              'Chọn chính sách giá bán để áp dụng cho khách hàng của bạn',
            placement: 'bottomRight',
            duration: 10,
            icon: <InfoCircleOutlined style={{ color: '#1890FF' }} />,
            style: {
              border: '1px solid #91d5ff',
              backgroundColor: '#e6f7ff',
            },
          });
        case 'PROPOSAL_POLICY':
          return notification.info({
            message: `Chính sách dự báo`,
            description:
              'Chọn chích sách báo hàng để áp dụng cho khách hàng của bạn',
            placement: 'bottomRight',
            duration: 10,
            icon: <InfoCircleOutlined style={{ color: '#1890FF' }} />,
            style: {
              border: '1px solid #91d5ff',
              backgroundColor: '#e6f7ff',
            },
          });
        default:
          return null;
      }
    }
    return null;
  };

  return (
    <>
      <Form
        form={form}
        layout="vertical"
        name="normal_login"
        style={{ width: 420 }}
        onValuesChange={handleFormChange}
      >
        <Row id="policy-list" justifyContent="space-between">
          <Form.Item
            label={<NameText>Tìm đối tác</NameText>}
            name="entityID"
            rules={[
              {
                required: true,
                message: 'Bạn chưa chọn đối tác!',
              },
            ]}
            style={{
              width: '100%',
            }}
          >
            <CSelect
              allowClear
              showSearch
              filterOption={false}
              notFoundContent={
                isLoadingSearchPartner ? (
                  <Spin size="small" />
                ) : (
                  <Empty
                    description="Hiện không có đối tác nào"
                    img={dataEmpty.ACCOUNT}
                    minHeight={120}
                  />
                )
              }
              optionLabelProp="label"
              placeholder="Nhập tên hoặc số điện thoại đối tác"
              width="100%"
              onFocus={() => handleFocus('NAME')}
              onSearch={setKeySearchPartner}
            >
              {partnerList.map((item: any) => (
                <SelectOption
                  key={item.id}
                  label={item.displayName}
                  value={item.id}
                >
                  <InfoCard
                    entityType={item.type}
                    img={item.avatar}
                    info={<PhoneNumberText phoneNumber={item.phoneNumber} />}
                    name={item.displayName}
                    size={SIZE.SMALL}
                    width="100%"
                  />
                </SelectOption>
              ))}
            </CSelect>
          </Form.Item>
        </Row>
        <Form.Item
          label="SKU đối tác"
          name="partnerCode"
          rules={[
            {
              required: true,
              message: 'Bạn chưa nhập SKU đối tác!',
            },
            ({ getFieldValue }) => {
              return {
                validator(_, value) {
                  if (value && value.length > 10) {
                    return Promise.reject('SKU không quá 10 ký tự!');
                  }
                  if (value && value.length > 0 && checkOnlySpaces(value)) {
                    return Promise.reject('SKU đối tác không dùng chuỗi rỗng!');
                  }
                  if (
                    value &&
                    value.length > 0 &&
                    checkFirstCharIsNotSpace(value)
                  ) {
                    return Promise.reject(
                      'SKU đối tác không được bắt đầu là khoảng trắng!'
                    );
                  }

                  return Promise.resolve();
                },
              };
            },
          ]}
          style={{
            width: '100%',
          }}
        >
          <CInput
            height={theme.input.height.md}
            maxLength={10}
            placeholder="Mã SKU đối tác"
            onFocus={() => handleFocus('SKU')}
          />
        </Form.Item>
        <Form.Item
          initialValue={
            genTypeList(
              entityType,
              selectedPartner.type,
              selectedPartner.validRole
            )[0].value
          }
          label={
            <Row>
              <NameText color="main">{form.getFieldValue('fullName')}</NameText>
              <Space width={3} />
              <NameText>liên kết với vai trò</NameText>
            </Row>
          }
          name="type"
          style={{
            width: '100%',
          }}
        >
          <CSelect
            disabled={
              genTypeList(
                entityType,
                selectedPartner.type,
                selectedPartner.validRole
              ).length === 1
            }
            list={genTypeList(entityType, selectedPartner)}
            placeholder="Chọn vai trò liên kết"
            onFocus={() => handleFocus('ROLL')}
          />
        </Form.Item>
        <Form.Item
          label="Lời giới thiệu"
          name="introduction"
          style={{
            width: '100%',
          }}
        >
          <Input.TextArea
            autoSize={{
              minRows: 4,
              maxRows: 6,
            }}
            onFocus={() => handleFocus('INTRODUCE')}
          />
        </Form.Item>
        {currentPartnerID !== '' &&
          currentPartnerGroupType === GROUP_TYPE.CUSTOMER && (
            <Form.Item
              initialValue={shouldCreateDigitalMenu}
              name="shouldCreateDigitalMenu"
              style={{
                width: '100%',
                marginBottom: 0,
              }}
              valuePropName="checked"
            >
              <Checkbox
                checked={shouldCreateDigitalMenu}
                styles={{ fontSize: 18 }}
                onChange={() =>
                  setShouldCreateDigitalMenu(!shouldCreateDigitalMenu)
                }
              >
                Tự động tạo menu đặt hàng
              </Checkbox>
            </Form.Item>
          )}
        <Space height={24} />
      </Form>
    </>
  );
};

export default PartnerInfoStep;

interface PartnerInfoStepProps {
  form: any;
  partnerList: any[];
  setPartnerList: (e: any[]) => void;
  currentPartnerGroupType: number;
  setCurrentPartnerGroupType: (e: number) => void;
  currentPartnerID: string;
  setCurrentPartnerID: (e: string) => void;
  selectedPartner: any;
  setSelectedPartner: (e: any) => void;
  setKeySearchPartner: (e: string) => void;
  isLoadingSearchPartner: boolean;
  setSelectedProducts: (e: string[]) => void;
}
